import { HeightRangePair, LimitPair, FuzzyDate } from "../../system";
import { AbstractRectangleItem } from "./AbstractRectangleItem";
import { TypeKeys } from "./TypeKeys";
import { MuscularStrengthInputKinds } from "../MuscularStrengthInputKinds";
import { MuscularStrengthValueKinds } from "../MuscularStrengthValueKinds";
import { GraphSubItem } from "./GraphSubItem";
import { GraphScaleKinds } from "../GraphScaleKinds";
import { ObjectId } from "../ObjectId";
import { IItem } from "./IItem";
import { AbstractValuedItem } from "./AbstractValuedItem";
import { BinaryFinder } from "../../../utilities";

export class GraphItem extends AbstractRectangleItem {
  constructor() {
    super(TypeKeys.GraphItem);
  }

  name: string;
  limitOfValue: LimitPair<number>;
  rangeOfValue: HeightRangePair<number>;
  muscularStrengthInputKind: MuscularStrengthInputKinds;
  muscularStrengthValueKind: MuscularStrengthValueKinds;
  graphSubItems: GraphSubItem[];
  scaleKind: GraphScaleKinds;
  shouldDrawBorder: boolean;
  // 10が指定されているとき→10分割で水平のグリッド線を表示する
  horizontalDivideCount: number;
  referenceIds: string[];
 
  protected createCloneInstance(): GraphItem {
    return new GraphItem();
  }

  protected applyMembers(clone: GraphItem): void {
    super.applyMembers(clone);
    clone.name = this.cloneValue(this.name);
    clone.limitOfValue = this.cloneValue(this.limitOfValue);
    clone.rangeOfValue = this.cloneValue(this.rangeOfValue);
    clone.muscularStrengthInputKind = this.cloneValue(this.muscularStrengthInputKind);
    clone.muscularStrengthValueKind = this.cloneValue(this.muscularStrengthValueKind);
    clone.graphSubItems = this.cloneValue(this.graphSubItems);
    clone.scaleKind = this.cloneValue(this.scaleKind);
    clone.shouldDrawBorder = this.cloneValue(this.shouldDrawBorder);
    clone.horizontalDivideCount = this.cloneValue(this.horizontalDivideCount);
    clone.referenceIds = this.cloneValue(this.referenceIds);
  }

  initialize(
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.initialize(itemAccessor, itemsAccessor) as GraphItem,
      s => s.createReevaluatedClone(itemAccessor, itemsAccessor),
    );
  }

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.handleItemUpdated(originalItem, updatedItem, isInSameSection, itemAccessor, itemsAccessor) as GraphItem,
      s => s.createReevaluatedCloneOnItemUpdated(updatedItem, itemAccessor, itemsAccessor),
    );
  }

  private createReevaluatedCloneOnItemUpdated(updatedItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): GraphItem {
    return (this.isReferedItem(updatedItem))
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private getReevaluatedClone(...updates: ((s: GraphItem) => GraphItem)[]): GraphItem {
    return this.getUpdatedClone<GraphItem>(...updates);
  }

  private createReevaluatedClone(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): GraphItem {
    const subItems = this.updateGraphSubItems(itemAccessor, itemsAccessor);
    return (this.isUpdatedSubItems(subItems))
      ? this.createUpdatedClone<GraphItem>(s => s.graphSubItems = subItems)
      : null;
  }

  private isReferedItem(updatedItem: IItem): boolean {
    return !!GraphItem._idFinder.find(updatedItem.id, this.referenceIds);
  }

  private static readonly _idFinder = new BinaryFinder((s: string) => s);
  
  private updateGraphSubItems(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): GraphSubItem[] {
    if (!this.graphSubItems) { return null; }
    const updatedSubItems: GraphSubItem[] = [];
    for (const graphSubItem of this.graphSubItems) {
      const cloned = graphSubItem.clone();
      if (cloned.graphValues) {
        for (const clonedValueItem of cloned.graphValues) {
          clonedValueItem.date = this.getValueAsDate(itemAccessor, clonedValueItem.dateObjectId);
          clonedValueItem.value = this.getValueAsNumber(itemAccessor, clonedValueItem.valueObjectId);
        }
      }
      updatedSubItems.push(cloned);
    }
    return updatedSubItems;
  }

  private getValueAsDate(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean)) => IItem, objectId: string): Date {
    if (!objectId) { return null; }
    const found = itemAccessor(new ObjectId(objectId));
    if (!found || !(found instanceof AbstractValuedItem) || !found.value || !(found.value instanceof FuzzyDate)) { return null; }

    // stringのときはdateのgetter内部の処理でnullにしてくれている
    const date = found.value.date;
    if (!date) { return null; }
    // 日付の時に時刻が入っていると座標がずれるので対策している
    return found.value.isTime
      ? new Date(date)
      : new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  getValueAsNumber(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean)) => IItem, objectId: string): number {
    if (!objectId) { return null; }
    const found = itemAccessor(new ObjectId(objectId));
    if (!found || !(found instanceof AbstractValuedItem) || found.value == null || found.value === "") { return null; }
    if ((typeof found.value !== "string") && (typeof found.value !== "number")) { return null; }
    const converted = Number(found.value);
    if (isNaN(converted) || !isFinite(converted)) { return null; }
    return converted;
  }

  private isUpdatedSubItems(subItems: GraphSubItem[]): boolean {
    if (!this.graphSubItems) { return false; }
    try {
      for (let subItemIndex = 0; subItemIndex < this.graphSubItems.length; ++subItemIndex) {
        const subItem = this.graphSubItems[subItemIndex];
        if (!subItem.graphValues) { continue; }
        for (let valueItemIndex = 0; valueItemIndex < subItem.graphValues.length; ++ valueItemIndex) {
          const currentValue = this.graphSubItems[subItemIndex].graphValues[valueItemIndex];
          const newValue = subItems[subItemIndex].graphValues[valueItemIndex];
          if (currentValue.date !== newValue.date || currentValue.value !== newValue.value) { return true; }
        }
      }
      return false;
    } catch (e) {
      // 何か考慮漏れがあってエラーになった場合、最低限の表示はできるようにtrueを返却する
      console.warn(e);
      return true;
    }
  }
}
